import { reportShell, reportOptions } from '../../reports/utils/shell';
import { formatDateOnly } from '../../../common/utils/date';

// Informe reproductivo "por finca" (paridad con el formato del PDF de muestra,
// decisión 0048). Consume el bloque `report` de GET /admin/{clients,farms}/:id/stats
// (?view=report, admin-stats.md): totales globales + una sección por finca con su
// responsable, resumen y una tabla de receptoras/transferencias. Mono-anclado en
// la fecha de T.E.; el backend ya recomputa summary/totals/pregnancy_rate sobre el
// período. El front NO calcula nada crítico — solo maqueta. Misma metodología
// HTML→PDF (POST /admin/reports/pdf) que los demás informes.

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const num = (n) => (n == null ? '—' : new Intl.NumberFormat('es-CO').format(Number(n)));
const pct = (v) => (v == null ? '—' : `${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format(Number(v))}%`);
const fdate = (d) => (d ? formatDateOnly(d) : '—');

// status del transfer → etiqueta + colores del badge (Preñada/Transferida/Vacía).
const STATUS = {
    pregnant:    { label: 'Preñada',     bg: '#dcfce7', color: '#166534' },
    transferred: { label: 'Transferida', bg: '#fef3c7', color: '#92400e' },
    empty:       { label: 'Vacía',       bg: '#fee2e2', color: '#991b1b' },
};
const DX_LETTER = { positive: 'P', negative: 'V', ppl: 'PPL' };

// resultado de una palpación → letra (P/V/PPL) o '—' si no hay dx.
const dxCell = (dx) => (dx && dx.result ? (DX_LETTER[dx.result] || String(dx.result).toUpperCase()) : '—');

// Tarjetas de resumen (Total / Preñadas / % Preñez / Transferidas / Vacías).
function cards(s = {}) {
    const items = [
        ['Total', num(s.total)],
        ['Preñadas', num(s.pregnant)],
        ['% Preñez', pct(s.pregnancy_rate)],
        ['Transferidas', num(s.transferred)],
        ['Vacías', num(s.empty)],
    ];
    return `<div class="meta">${items.map(([l, v]) =>
        `<div class="meta-item" style="min-width:96px;text-align:center;"><strong>${esc(l)}</strong><span style="font-size:16px;">${v}</span></div>`).join('')}</div>`;
}

function statusBadge(status) {
    const st = STATUS[status] || { label: status || '—', bg: '#f1f5f9', color: '#475569' };
    return `<span class="badge" style="background:${st.bg};color:${st.color};">${esc(st.label)}</span>`;
}

function farmSection(f) {
    const farm = f.farm || {};
    const head = [esc(farm.name || '—'), farm.responsible ? esc(farm.responsible) : null].filter(Boolean).join(' · ');

    // Aplana todas las transferencias de la finca y las ordena por fecha de T.E. de
    // MAYOR A MENOR (más reciente primero). `te_date` es DATEONLY 'YYYY-MM-DD' → el
    // orden lexicográfico coincide con el cronológico; las sin fecha caen al final.
    const items = (f.recipients || [])
        .flatMap((rec) => (rec.transfers || []).map((t) => ({ rec, t })))
        .sort((a, b) => String(b.t.te_date || '').localeCompare(String(a.t.te_date || '')));

    const rows = items.map(({ rec, t }, i) => {
        const receptora = `${esc(rec.number ?? '—')}${rec.name ? ` · ${esc(rec.name)}` : ''}`;
        const parto = t.status === 'pregnant' ? fdate(t.due_date) : '—';
        return `<tr>
                <td>${i + 1}</td>
                <td style="text-align:left;"><strong>${receptora}</strong></td>
                <td style="text-align:left;">${esc(t.donor?.name || '—')}</td>
                <td style="text-align:left;">${esc(t.sire?.name || '—')}</td>
                <td>${esc(t.embryo?.code || '—')}</td>
                <td>${fdate(t.te_date)}</td>
                <td>${dxCell(t.dx1)}</td>
                <td>${dxCell(t.dx2)}</td>
                <td>${parto}</td>
                <td>${statusBadge(t.status)}</td>
            </tr>`;
    });

    return `
        <h2 class="report-section-title">${head}</h2>
        ${cards(f.summary)}
        <table>
            <thead><tr>
                <th>#</th><th>Receptora</th><th>Donadora</th><th>Toro</th><th>Emb.</th>
                <th>Fecha</th><th>DX1</th><th>DX2</th><th>Parto prob.</th><th>Estado</th>
            </tr></thead>
            <tbody>${rows.join('') || '<tr><td colspan="10">Sin transferencias en el período.</td></tr>'}</tbody>
        </table>`;
}

export function buildReproductiveReport(data) {
    const entity = data?.entity || {};
    const report = data?.report || {};
    const scopeLabel = entity.type === 'client' ? 'Cliente' : 'Finca';
    const p = report.period || {};
    const period = (p.from || p.to)
        ? `${p.from ? formatDateOnly(p.from) : '…'} — ${p.to ? formatDateOnly(p.to) : '…'}`
        : 'período total';
    const sub = `${scopeLabel}: ${esc(entity.name || '—')} · Periodo: ${period}${report.generated_at ? ` · Generado: ${formatDateOnly(report.generated_at)}` : ''}`;
    const farms = report.farms || [];

    const body = `
        <h1 class="report-title">Reporte reproductivo por finca</h1>
        <p class="report-subtitle">${sub}</p>

        <h2 class="report-section-title">Resumen general</h2>
        ${cards(report.totals)}

        ${farms.length ? farms.map(farmSection).join('') : '<p class="report-subtitle">Sin transferencias en el período.</p>'}

        <p style="margin-top:20px;text-align:center;font-size:9px;color:#9ca3af;line-height:1.6;">
            COWRE℠ v2 · Documento generado automáticamente<br/>
            Agropecuaria Los Manantiales S.A.S. · Nit: 901189031-9
        </p>`;

    const slug = String(entity.name || 'reproductivo').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return { html: reportShell({ bodyHtml: body }), filename: `informe-reproductivo-${slug || 'finca'}`, options: reportOptions() };
}
